import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import {
  AddMobileServiceInput,
  AddPermanentServiceInput,
  AdvancedCostParametersInput,
  FetchTenderByIdDocument,
  FetchTenderByIdQuery,
  FetchTenderByIdQueryVariables,
  MobileService,
  PermanentService,
  RevenueCalculationType,
  ServiceRatioInput,
  ServiceType,
  Shift,
} from "@/graphql/__generated__/graphql";
import {
  useAddCarMutationHook,
  useAddMobileServiceMutationHook,
  useAddPermanentServiceMutationHook,
  useAddPurchaseMutationHook,
  useEditMobileServiceMutationHook,
  useEditPermanentServiceMutationHook,
  useRemoveCarMutationHook,
  useRemovePurchaseMutationHook,
  useUpdateTenderContractDurationMutationHook,
} from "@/hooks";
import useRemoveServiceMutationHook from "@/hooks/mutations/useRemoveServiceMutation";
import { useFetchCarsOnTenderQueryHook, useFetchPurchasesOnTenderQueryHook, useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { normalizeDateToUtcMidnight } from "@/utils/helpers";
import { useQuery } from "@apollo/client/react";
import { format } from "date-fns";

enum ProgressState {
  // eslint-disable-next-line no-unused-vars
  LoadingServices = "loading-services",
  // eslint-disable-next-line no-unused-vars
  RemovingCarsAndPurchases = "removing-cars-and-purchases",
  // eslint-disable-next-line no-unused-vars
  RemovingServices = "removing-services",
  // eslint-disable-next-line no-unused-vars
  UpdatingContractDuration = "updating-contract-duration",
  // eslint-disable-next-line no-unused-vars
  RecalculatingServices = "recalculating-services",
  // eslint-disable-next-line no-unused-vars
  Done = "done",
  // eslint-disable-next-line no-unused-vars
  Failed = "failed"
}

type ServiceLink = Pick<ServiceRatioInput, "serviceId" | "ratio">;

type FailedRelinkAsset = {
  id: number;
  name: string;
  type: "car" | "purchase";
};

type CarSnapshot = {
  id: number;
  name: string;
  netValue: number;
  runtime: number;
  yearlyInsurance: number;
  yearlyTax: number;
  yearlyKilometers: number;
  consumption: number;
  fuelPrice: number;
  repairPerKilometer: number;
  quantity: number;
  margin: number;
  links: ServiceLink[];
};

type PurchaseSnapshot = {
  id: number;
  name: string;
  purchaseCost: number;
  monthsDepreciation: number;
  yearlyMaintenance: number;
  yearlyRepair: number;
  yearlyAdditionals: number;
  quantity: number;
  margin: number;
  links: ServiceLink[];
};

interface ServiceRecalculationDialogProps {
  tenderId: number;
  isOpen: boolean;
  setIsOpen: (_isOpen: boolean) => void;
  contractDurationValues: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  };
  setIsEditing: (_isEditing: boolean) => void;
}

/**
 * Dialog for recalculating the services on a tender after a contract duration change when there is services.
 * Works by removing all services, updating the contract duration and then re-adding the services.
 * @param tenderId - The ID of the tender
 * @param isOpen - Whether the dialog is open
 * @param setIsOpen - Function to set the dialog open state
 * @param contractDurationValues - The contract duration values
 * @param setIsEditing - Function to set the editing state
 * @returns The ServiceRecalculationDialog component
 */
function ServiceRecalculationDialog({ tenderId, isOpen, setIsOpen, contractDurationValues, setIsEditing }: ServiceRecalculationDialogProps) {
  const { t } = useTranslation();

  const options = {
    disableRefetch: true,
    disableToast: true
  };

  // Queries
  const { data, loading: servicesLoading } = useFetchServicesOnTenderQueryHook(Number(tenderId), false, true);
  const { data: carsData, loading: carsLoading } = useFetchCarsOnTenderQueryHook(Number(tenderId));
  const { data: purchasesData, loading: purchasesLoading } = useFetchPurchasesOnTenderQueryHook(Number(tenderId));
  const { data: _tenderData, loading: _tenderDataLoading, refetch: tenderDataRefetch } = useQuery<FetchTenderByIdQuery, FetchTenderByIdQueryVariables>(FetchTenderByIdDocument, {
    variables: {
      id: Number(tenderId)
    },
    skip: true
  });

  // Mutations
  const { removeServiceMutation } = useRemoveServiceMutationHook(0, options);
  const { removeCarMutation } = useRemoveCarMutationHook({ disableToast: true, disableRefetch: true });
  const { removePurchaseMutation } = useRemovePurchaseMutationHook({ disableToast: true, disableRefetch: true });
  const { updateTenderContractDurationMutation } = useUpdateTenderContractDurationMutationHook(options);
  const { addPermanentServiceMutation } = useAddPermanentServiceMutationHook(options);
  const { addMobileServiceMutation } = useAddMobileServiceMutationHook(options);
  const { addCarMutation } = useAddCarMutationHook(options);
  const { addPurchaseMutation } = useAddPurchaseMutationHook(options);
  const { editPermanentServiceMutation } = useEditPermanentServiceMutationHook(options);
  const { editMobileServiceMutation } = useEditMobileServiceMutationHook(options);

  const services = data?.fetchServicesOnTender?.services ?? [];

  // Refs
  const processedRef = useRef(false);
  const processedCarsAndPurchasesRef = useRef(false);
  const completedRemovalsRef = useRef(0);
  const completedCarPurchaseRemovalsRef = useRef(0);
  const completedRecalculationsRef = useRef(0);
  const isProcessingRef = useRef(false);
  const serviceIdMapRef = useRef<Map<number, number>>(new Map());
  const carLinksByCarIdRef = useRef<Map<number, { name: string; links: ServiceLink[] }>>(new Map());
  const purchaseLinksByPurchaseIdRef = useRef<Map<number, { name: string; links: ServiceLink[] }>>(new Map());
  const carsSnapshotRef = useRef<CarSnapshot[]>([]);
  const purchasesSnapshotRef = useRef<PurchaseSnapshot[]>([]);

  const [progressState, setProgressState] = useState<ProgressState>(ProgressState.LoadingServices);
  const [currentServiceName, setCurrentServiceName] = useState<string>("");
  const [failedToRecalculate, setFailedToRecalculate] = useState<{ id: number, name: string }[]>([]);
  const [failedToRelinkAssets, setFailedToRelinkAssets] = useState<FailedRelinkAsset[]>([]);
  const [failedToRemoveCarsPurchases, setFailedToRemoveCarsPurchases] = useState<FailedRelinkAsset[]>([]);

  const hasIssues = failedToRecalculate.length > 0 || failedToRelinkAssets.length > 0 || failedToRemoveCarsPurchases.length > 0;
  const progressIndicatorClassName =
    progressState === ProgressState.Done ? (hasIssues ? "bg-red-500" : "bg-green-500") : "bg-yellow-500";

  // Reset refs when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      processedRef.current = false;
      processedCarsAndPurchasesRef.current = false;
      completedRemovalsRef.current = 0;
      completedCarPurchaseRemovalsRef.current = 0;
      completedRecalculationsRef.current = 0;
      isProcessingRef.current = false;
      serviceIdMapRef.current = new Map();
      carLinksByCarIdRef.current = new Map();
      purchaseLinksByPurchaseIdRef.current = new Map();
      carsSnapshotRef.current = [];
      purchasesSnapshotRef.current = [];
      setProgressState(ProgressState.LoadingServices);
      setFailedToRecalculate([]);
      setFailedToRelinkAssets([]);
      setFailedToRemoveCarsPurchases([]);
      setCurrentServiceName("");
    }
  }, [isOpen]);

  /**
   * Retries a mutation function with delay between attempts
   * @template T The return type of the mutation function
   * @param {() => Promise<T>} mutationFn The mutation function to retry
   * @param {number} [maxRetries=3] Maximum number of retry attempts
   * @param {number} [delay=1000] Base delay between retries in milliseconds
   * @returns {Promise<T>} The result of the successful mutation
   * @throws {Error} The last error encountered if all retries fail
   */
  const retryMutation = async <T,>(
    mutationFn: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> => {
    // Track the last error encountered for final error reporting
    let lastError: Error | null = null;

    // Attempt the mutation up to maxRetries times
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Execute the mutation and return its result if successful
        return await mutationFn();
      } catch (error) {
        // Store the error and wait before retrying if not on last attempt
        lastError = error as Error;
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // If all retries failed, throw the last error encountered
    throw lastError;
  };

  /**
   * Handles the progress state of the dialog
   */
  useEffect(() => {
    // If the dialog is not open or the processing is in progress, return
    if (!isOpen || isProcessingRef.current) return;

    // Step 1: Handle loading services
    if (!servicesLoading && !carsLoading && !purchasesLoading && progressState === ProgressState.LoadingServices) {
      // Snapshot current car/purchase data (full data, not just links)
      const permanentServicesForSnapshot = services.filter(
        (service): service is PermanentService => service.type === ServiceType.Permanent
      );

      // Snapshot cars with full data
      const carLinksMap = new Map<number, { name: string; links: ServiceLink[] }>();
      const carsSnapshot: CarSnapshot[] = [];
      const carIdsSeen = new Set<number>();

      permanentServicesForSnapshot.forEach((service) => {
        (service.cars ?? []).forEach((carDetails) => {
          const carId = carDetails.car.id;

          if (!carIdsSeen.has(carId)) {
            carIdsSeen.add(carId);
            carsSnapshot.push({
              id: carId,
              name: carDetails.car.name,
              netValue: Number(carDetails.car.netValue),
              runtime: carDetails.car.runtime,
              yearlyInsurance: Number(carDetails.car.yearlyInsurance),
              yearlyTax: Number(carDetails.car.yearlyTax),
              yearlyKilometers: Number(carDetails.car.yearlyKilometers),
              consumption: Number(carDetails.car.consumption),
              fuelPrice: Number(carDetails.car.fuelPrice),
              repairPerKilometer: Number(carDetails.car.repairPerKilometer),
              quantity: carDetails.car.quantity,
              margin: carDetails.car.margin,
              links: (carDetails.links ?? []).map((link) => ({
                serviceId: link.serviceId,
                ratio: link.ratio,
              })),
            });
          } else {
            // Merge links if car already seen
            const existingCar = carsSnapshot.find(c => c.id === carId);
            if (existingCar) {
              const newLinks = (carDetails.links ?? []).map((link) => ({
                serviceId: link.serviceId,
                ratio: link.ratio,
              }));
              const mergedLinksByServiceId = new Map<number, ServiceLink>();
              existingCar.links.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
              newLinks.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
              existingCar.links = Array.from(mergedLinksByServiceId.values());
            }
          }

          // Also maintain the old map for backward compatibility during transition
          const existing = carLinksMap.get(carId);
          const nextLinks = (carDetails.links ?? []).map((link) => ({
            serviceId: link.serviceId,
            ratio: link.ratio,
          }));

          if (!existing) {
            carLinksMap.set(carId, {
              name: carDetails.car.name,
              links: nextLinks,
            });
          } else {
            const mergedLinksByServiceId = new Map<number, ServiceLink>();
            existing.links.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
            nextLinks.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
            existing.links = Array.from(mergedLinksByServiceId.values());
          }
        });
      });

      // Also include standalone cars (cars not linked to any service)
      const allCars = carsData?.fetchCarsOnTender ?? [];
      allCars.forEach((carDetail) => {
        if (!carIdsSeen.has(carDetail.car.id)) {
          carIdsSeen.add(carDetail.car.id);
          carsSnapshot.push({
            id: carDetail.car.id,
            name: carDetail.car.name,
            netValue: Number(carDetail.car.netValue),
            runtime: carDetail.car.runtime,
            yearlyInsurance: Number(carDetail.car.yearlyInsurance),
            yearlyTax: Number(carDetail.car.yearlyTax),
            yearlyKilometers: Number(carDetail.car.yearlyKilometers),
            consumption: Number(carDetail.car.consumption),
            fuelPrice: Number(carDetail.car.fuelPrice),
            repairPerKilometer: Number(carDetail.car.repairPerKilometer),
            quantity: carDetail.car.quantity,
            margin: carDetail.car.margin,
            links: (carDetail.links ?? []).map((link) => ({
              serviceId: link.serviceId,
              ratio: link.ratio,
            })),
          });
        }
      });

      carsSnapshotRef.current = carsSnapshot;
      carLinksByCarIdRef.current = carLinksMap;

      // Snapshot purchases with full data
      const purchaseLinksMap = new Map<number, { name: string; links: ServiceLink[] }>();
      const purchasesSnapshot: PurchaseSnapshot[] = [];
      const purchaseIdsSeen = new Set<number>();

      permanentServicesForSnapshot.forEach((service) => {
        (service.purchases ?? []).forEach((purchaseDetails) => {
          const purchaseId = purchaseDetails.purchase.id;

          if (!purchaseIdsSeen.has(purchaseId)) {
            purchaseIdsSeen.add(purchaseId);
            purchasesSnapshot.push({
              id: purchaseId,
              name: purchaseDetails.purchase.name,
              purchaseCost: Number(purchaseDetails.purchase.purchaseCost),
              monthsDepreciation: purchaseDetails.purchase.monthsDepreciation,
              yearlyMaintenance: Number(purchaseDetails.purchase.yearlyMaintenance),
              yearlyRepair: Number(purchaseDetails.purchase.yearlyRepair),
              yearlyAdditionals: Number(purchaseDetails.purchase.yearlyAdditionals),
              quantity: purchaseDetails.purchase.quantity,
              margin: purchaseDetails.purchase.margin,
              links: (purchaseDetails.links ?? []).map((link) => ({
                serviceId: link.serviceId,
                ratio: link.ratio,
              })),
            });
          } else {
            // Merge links if purchase already seen
            const existingPurchase = purchasesSnapshot.find(p => p.id === purchaseId);
            if (existingPurchase) {
              const newLinks = (purchaseDetails.links ?? []).map((link) => ({
                serviceId: link.serviceId,
                ratio: link.ratio,
              }));
              const mergedLinksByServiceId = new Map<number, ServiceLink>();
              existingPurchase.links.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
              newLinks.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
              existingPurchase.links = Array.from(mergedLinksByServiceId.values());
            }
          }

          // Also maintain the old map for backward compatibility during transition
          const existing = purchaseLinksMap.get(purchaseId);
          const nextLinks = (purchaseDetails.links ?? []).map((link) => ({
            serviceId: link.serviceId,
            ratio: link.ratio,
          }));

          if (!existing) {
            purchaseLinksMap.set(purchaseId, {
              name: purchaseDetails.purchase.name,
              links: nextLinks,
            });
          } else {
            const mergedLinksByServiceId = new Map<number, ServiceLink>();
            existing.links.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
            nextLinks.forEach((link) => mergedLinksByServiceId.set(link.serviceId, link));
            existing.links = Array.from(mergedLinksByServiceId.values());
          }
        });
      });

      // Also include standalone purchases (purchases not linked to any service)
      const allPurchases = purchasesData?.fetchPurchasesOnTender ?? [];
      allPurchases.forEach((purchaseDetail) => {
        if (!purchaseIdsSeen.has(purchaseDetail.purchase.id)) {
          purchaseIdsSeen.add(purchaseDetail.purchase.id);
          purchasesSnapshot.push({
            id: purchaseDetail.purchase.id,
            name: purchaseDetail.purchase.name,
            purchaseCost: Number(purchaseDetail.purchase.purchaseCost),
            monthsDepreciation: purchaseDetail.purchase.monthsDepreciation,
            yearlyMaintenance: Number(purchaseDetail.purchase.yearlyMaintenance),
            yearlyRepair: Number(purchaseDetail.purchase.yearlyRepair),
            yearlyAdditionals: Number(purchaseDetail.purchase.yearlyAdditionals),
            quantity: purchaseDetail.purchase.quantity,
            margin: purchaseDetail.purchase.margin,
            links: (purchaseDetail.links ?? []).map((link) => ({
              serviceId: link.serviceId,
              ratio: link.ratio,
            })),
          });
        }
      });

      purchasesSnapshotRef.current = purchasesSnapshot;
      purchaseLinksByPurchaseIdRef.current = purchaseLinksMap;

      // Move to removing cars/purchases if there are any, otherwise go directly to removing services
      if (carsSnapshot.length > 0 || purchasesSnapshot.length > 0) {
        setProgressState(ProgressState.RemovingCarsAndPurchases);
      } else {
        setProgressState(ProgressState.RemovingServices);
      }
    }

    // Step 2: Handle removing cars and purchases
    if (progressState === ProgressState.RemovingCarsAndPurchases && !processedCarsAndPurchasesRef.current) {
      processedCarsAndPurchasesRef.current = true;
      completedCarPurchaseRemovalsRef.current = 0;

      const removeAllCarsAndPurchases = async () => {
        const failedRemovals: FailedRelinkAsset[] = [];

        // Remove all cars
        for (const car of carsSnapshotRef.current) {
          try {
            await retryMutation(
              () =>
                new Promise<void>((resolve, reject) => {
                  removeCarMutation({
                    variables: {
                      tenderId: Number(tenderId),
                      carId: car.id,
                    },
                    onCompleted: () => resolve(),
                    onError: (error) => reject(error),
                  });
                })
            );
            completedCarPurchaseRemovalsRef.current += 1;
          } catch (error) {
            console.error(`Failed to remove car ${car.id} after 3 attempts:`, error);
            failedRemovals.push({ id: car.id, name: car.name, type: "car" });
            completedCarPurchaseRemovalsRef.current += 1;
          }
        }

        // Remove all purchases
        for (const purchase of purchasesSnapshotRef.current) {
          try {
            await retryMutation(
              () =>
                new Promise<void>((resolve, reject) => {
                  removePurchaseMutation({
                    variables: {
                      tenderId: Number(tenderId),
                      purchaseId: purchase.id,
                    },
                    onCompleted: () => resolve(),
                    onError: (error) => reject(error),
                  });
                })
            );
            completedCarPurchaseRemovalsRef.current += 1;
          } catch (error) {
            console.error(`Failed to remove purchase ${purchase.id} after 3 attempts:`, error);
            failedRemovals.push({ id: purchase.id, name: purchase.name, type: "purchase" });
            completedCarPurchaseRemovalsRef.current += 1;
          }
        }

        if (failedRemovals.length > 0) {
          setFailedToRemoveCarsPurchases(failedRemovals);
          // Continue anyway - we'll try to recreate what we can
        }

        // Move to removing services regardless of failures
        setProgressState(ProgressState.RemovingServices);
      };

      void removeAllCarsAndPurchases();
    }

    // Step 3: Handle removing services
    if (progressState === ProgressState.RemovingServices && !processedRef.current) {
      processedRef.current = true;
      completedRemovalsRef.current = 0;

      const removeAllServices = async () => {
        for (const service of services) {
          try {
            await retryMutation(
              () =>
                new Promise<void>((resolve, reject) => {
                  removeServiceMutation(
                    {
                      serviceId: service.id,
                    },
                    {
                      onCompleted: () => resolve(),
                      onError: (error) => reject(error),
                    }
                  );
                })
            );
            completedRemovalsRef.current += 1;
          } catch (error) {
            console.error(`Failed to remove service ${service.id} after 3 attempts:`, error);
            setFailedToRecalculate((prev) => [...prev, { id: service.id, name: service.name }]);
            setProgressState(ProgressState.Failed);
            return;
          }
        }

        setProgressState(ProgressState.UpdatingContractDuration);
      };

      void removeAllServices();
    }

    // Step 4: Handle updating contract duration
    if (progressState === ProgressState.UpdatingContractDuration && !isProcessingRef.current && failedToRecalculate.length === 0) {
      isProcessingRef.current = true;
      retryMutation(() =>
        new Promise<void>((resolveUpdate, rejectUpdate) => {
          updateTenderContractDurationMutation({
            variables: {
              tenderId: Number(tenderId),
              startDate: normalizeDateToUtcMidnight(contractDurationValues.startDate)?.toISOString(),
              endDate: normalizeDateToUtcMidnight(contractDurationValues.endDate)?.toISOString()
            },
            onCompleted: () => {
              resolveUpdate();
              setProgressState(ProgressState.RecalculatingServices);
            },
            onError: (error) => rejectUpdate(error)
          });
        })
      ).catch(error => {
        console.error("Failed to update contract duration after 3 attempts:", error);
        // Continue with recalculation even if update fails
        setProgressState(ProgressState.RecalculatingServices);
      }).finally(() => {
        isProcessingRef.current = false;
      });
    }

    // Step 5: Handle recalculating services
    if (progressState === ProgressState.RecalculatingServices && completedRecalculationsRef.current === 0 && !isProcessingRef.current) {
      isProcessingRef.current = true;
      completedRecalculationsRef.current = 0;
      serviceIdMapRef.current = new Map();
      const permanentServices = services.filter((service): service is PermanentService => service.type === ServiceType.Permanent);
      const mobileServices = services.filter((service): service is MobileService => service.type === ServiceType.Mobile);

      // Process all services sequentially
      const processServices = async () => {
        try {
          // Process permanent services
          for (const service of permanentServices) {
            await new Promise<void>((resolve) => {
              setCurrentServiceName(service.name);
              retryMutation(() =>
                new Promise<void>((resolveMutation, rejectMutation) => {
                  addPermanentServiceMutation({
                    variables: {
                      tenderId: Number(tenderId),
                      input: {
                        additional: service.additional,
                        hourlyWage: service.hourlyWage,
                        ...(service.revenueCalculationType === RevenueCalculationType.Flatrate && { flatRate: service.flatRate }),
                        // ***NOTE : Apollo Client adds __typename to cached objects, but GraphQL Input types don't accept it.***
                        // We must manually reconstruct hourlyRateAbsolute/hourlyRateRelative objects field-by-field
                        // to strip __typename before sending to the mutation.
                        ...(service.revenueCalculationType === RevenueCalculationType.Hourlyrateabsolute && service.hourlyRateAbsolute && {
                          hourlyRateAbsolute: {
                            hourlyRateDayAbsolute: service.hourlyRateAbsolute.hourlyRateDayAbsolute,
                            hourlyRateNightAbsolute: service.hourlyRateAbsolute.hourlyRateNightAbsolute,
                            hourlyRateSundayAbsolute: service.hourlyRateAbsolute.hourlyRateSundayAbsolute,
                            hourlyRateSundayNightAbsolute: service.hourlyRateAbsolute.hourlyRateSundayNightAbsolute,
                            hourlyRateHolidayAbsolute: service.hourlyRateAbsolute.hourlyRateHolidayAbsolute,
                            hourlyRateHolidayNightAbsolute: service.hourlyRateAbsolute.hourlyRateHolidayNightAbsolute,
                          }
                        }),
                        ...(service.revenueCalculationType === RevenueCalculationType.Hourlyraterelative && service.hourlyRateRelative && {
                          hourlyRateRelative: {
                            hourlyRateDayAbsolute: service.hourlyRateRelative.hourlyRateDayAbsolute,
                            hourlyRateNightRelative: service.hourlyRateRelative.hourlyRateNightRelative,
                            hourlyRateSundayRelative: service.hourlyRateRelative.hourlyRateSundayRelative,
                            hourlyRateSundayNightRelative: service.hourlyRateRelative.hourlyRateSundayNightRelative,
                            hourlyRateHolidayRelative: service.hourlyRateRelative.hourlyRateHolidayRelative,
                            hourlyRateHolidayNightRelative: service.hourlyRateRelative.hourlyRateHolidayNightRelative,
                          }
                        }),
                        ...(service.revenueCalculationType === RevenueCalculationType.Margin && { margin: service.margin }),
                        hoursBriefingPerEmployee: service.hoursBriefingPerEmployee,
                        info: service.info,
                        name: service.name,
                        nonTariffAllowance: service.nonTariffAllowance,
                        operationalOrganizationId: service.operationalOrganizationId,
                        pauseIncluded: service.pauseIncluded,
                        payGroup: service.payGroup,
                        revenueCalculationType: service.revenueCalculationType,
                        transferredEmployeePercentage: service.transferredEmployeePercentage,
                        shifts: service.shifts?.map((shift: Shift) => ({
                          employeeQuantity: shift.employeeQuantity,
                          subContractorQuantity: shift.subContractorQuantity,
                          startTime: shift.startTime,
                          endTime: shift.endTime,
                          holiday: shift.holiday,
                          monday: shift.monday,
                          tuesday: shift.tuesday,
                          wednesday: shift.wednesday,
                          thursday: shift.thursday,
                          friday: shift.friday,
                          saturday: shift.saturday,
                          sunday: shift.sunday,
                          applyHolidayPay: shift.applyHolidayPay,
                          ...(shift.startTime && { startTime: shift.startTime }),
                          ...(shift.endTime && { endTime: shift.endTime }),
                          ...(shift.pauseStartTime && { pauseStartTime: shift.pauseStartTime }),
                          ...(shift.pauseEndTime && { pauseEndTime: shift.pauseEndTime }),
                          ...(shift.startDate && { startDate: typeof shift.startDate === "string" ? shift.startDate : format(shift.startDate, "yyyy-MM-dd") }),
                          ...(shift.endDate && { endDate: typeof shift.endDate === "string" ? shift.endDate : format(shift.endDate, "yyyy-MM-dd") })
                        })),
                        siteId: service.siteId
                      } satisfies AddPermanentServiceInput
                    },
                    onCompleted: async (data) => {
                      const newServiceId = data.addPermanentService.id;

                      // Track old->new id mapping so we can relink cars/purchases after all services are recreated.
                      serviceIdMapRef.current.set(service.id, newServiceId);

                      // Edit permanent service to add AdvancedCostParameters
                      await retryMutation(() =>
                        new Promise<void>((resolveEdit, rejectEdit) => {
                          editPermanentServiceMutation({
                            variables: {
                              serviceId: newServiceId,
                              input: {
                                acp: {
                                  averageHoursWorkedPerMonth: service.advancedCostParameters.averageHoursWorkedPerMonth,
                                  averageLengthOfEmployment: service.advancedCostParameters.averageLengthOfEmployment,
                                  sickDays: service.advancedCostParameters.sickDays,
                                  trainingDays: service.advancedCostParameters.trainingDays,
                                  yearlyTrainingCosts: Number(service.advancedCostParameters.yearlyTrainingCosts),
                                  employeeFluctuation: service.advancedCostParameters.employeeFluctuation,
                                  yearlyUniformCosts: Number(service.advancedCostParameters.yearlyUniformCosts),
                                  otherMaterialCosts: Number(service.advancedCostParameters.otherMaterialCosts)
                                } satisfies Omit<AdvancedCostParametersInput, "carCostsPerMinute" | "otherCostsPerMinute">
                              }
                            },
                            onCompleted: () => resolveEdit(),
                            onError: (error) => rejectEdit(error)
                          });
                        })
                      ).catch(error => {
                        console.error(`Failed to edit permanent service ${newServiceId} after 3 attempts:`, error);
                      });

                      completedRecalculationsRef.current += 1;
                      resolveMutation();
                    },
                    onError: (error) => rejectMutation(error)
                  });
                })
              ).catch(error => {
                console.error("Failed to add permanent service after 3 attempts:", error);

                completedRecalculationsRef.current += 1;

                setFailedToRecalculate(prev => [...prev, { id: service.id, name: service.name }]);
              }).finally(() => {
                resolve();
              });
            });
          }

          // process mobile services
          for (const service of mobileServices) {
            await new Promise<void>((resolve) => {
              setCurrentServiceName(service.name);
              retryMutation(() =>
                new Promise<void>((resolveMutation, rejectMutation) => {
                  addMobileServiceMutation({
                    variables: {
                      tenderId: Number(tenderId),
                      input: {
                        additional: service.additional,
                        hourlyWage: service.hourlyWage,
                        hoursBriefingPerEmployee: service.hoursBriefingPerEmployee,
                        info: service.info,
                        name: service.name,
                        nonTariffAllowance: service.nonTariffAllowance,
                        operationalOrganizationId: service.operationalOrganizationId,
                        payGroup: service.payGroup,
                        siteId: service.siteId,
                        siteVisits: service.siteVisits.map((visit) => ({
                          duration: visit.duration,
                          margin: visit.margin,
                          type: visit.type,
                          mondayDay: visit.mondayDay,
                          mondayNight: visit.mondayNight,
                          tuesdayDay: visit.tuesdayDay,
                          tuesdayNight: visit.tuesdayNight,
                          wednesdayDay: visit.wednesdayDay,
                          wednesdayNight: visit.wednesdayNight,
                          thursdayDay: visit.thursdayDay,
                          thursdayNight: visit.thursdayNight,
                          fridayDay: visit.fridayDay,
                          fridayNight: visit.fridayNight,
                          saturdayDay: visit.saturdayDay,
                          saturdayNight: visit.saturdayNight,
                          sundayDay: visit.sundayDay,
                          sundayNight: visit.sundayNight,
                          holidayDay: visit.holidayDay,
                          holidayNight: visit.holidayNight,
                          ...(visit.startDate && { startDate: typeof visit.startDate === "string" ? visit.startDate : format(new Date(visit.startDate), "yyyy-MM-dd") }),
                          ...(visit.endDate && { endDate: typeof visit.endDate === "string" ? visit.endDate : format(new Date(visit.endDate), "yyyy-MM-dd") })
                        })),
                        travelTime: service.travelTime
                      } satisfies AddMobileServiceInput
                    },
                    onCompleted: async (data) => {
                      const newServiceId = data.addMobileService.id;

                      // Edit mobile service to add AdvancedCostParameters
                      await retryMutation(() =>
                        new Promise<void>((resolveEdit, rejectEdit) => {
                          editMobileServiceMutation({
                            variables: {
                              serviceId: newServiceId,
                              input: {
                                acp: {
                                  averageHoursWorkedPerMonth: service.advancedCostParameters.averageHoursWorkedPerMonth,
                                  averageLengthOfEmployment: service.advancedCostParameters.averageLengthOfEmployment,
                                  sickDays: service.advancedCostParameters.sickDays,
                                  trainingDays: service.advancedCostParameters.trainingDays,
                                  yearlyTrainingCosts: Number(service.advancedCostParameters.yearlyTrainingCosts),
                                  employeeFluctuation: service.advancedCostParameters.employeeFluctuation,
                                  yearlyUniformCosts: Number(service.advancedCostParameters.yearlyUniformCosts),
                                  otherMaterialCosts: Number(service.advancedCostParameters.otherMaterialCosts),
                                  carCostsPerMinute: Number(service.advancedCostParameters.carCostsPerMinute),
                                  otherCostsPerMinute: Number(service.advancedCostParameters.otherCostsPerMinute)
                                } satisfies AdvancedCostParametersInput
                              }
                            },
                            onCompleted: () => resolveEdit(),
                            onError: (error) => rejectEdit(error)
                          });
                        })
                      ).catch(error => {
                        console.error(`Failed to edit mobile service ${newServiceId} after 3 attempts:`, error);
                      });

                      completedRecalculationsRef.current += 1;
                      resolveMutation();
                    },
                    onError: (error) => rejectMutation(error)
                  });
                })
              ).catch(error => {
                console.error("Failed to add mobile service after 3 attempts:", error);

                completedRecalculationsRef.current += 1;

                setFailedToRecalculate(prev => [...prev, { id: service.id, name: service.name }]);
              }).finally(() => {
                resolve();
              });
            });
          }

          // Recreate cars and purchases with new service links
          const newFailedAssets: FailedRelinkAsset[] = [];

          // Recreate cars
          for (const carSnapshot of carsSnapshotRef.current) {
            // Skip cars that failed to remove
            if (failedToRemoveCarsPurchases.some(f => f.id === carSnapshot.id && f.type === "car")) {
              continue;
            }

            const mappedLinks: ServiceLink[] = [];
            let hasMissingMapping = false;

            // Map old service IDs to new service IDs
            carSnapshot.links.forEach((link) => {
              const newServiceId = serviceIdMapRef.current.get(link.serviceId);
              if (!newServiceId) {
                hasMissingMapping = true;
                return;
              }
              mappedLinks.push({ serviceId: newServiceId, ratio: link.ratio });
            });

            if (hasMissingMapping && carSnapshot.links.length > 0) {
              // Only fail if there were links but we couldn't map them
              newFailedAssets.push({ id: carSnapshot.id, name: carSnapshot.name, type: "car" });
              continue;
            }

            try {
              await retryMutation(() =>
                new Promise<void>((resolve, reject) => {
                  addCarMutation({
                    variables: {
                      tenderId: Number(tenderId),
                      linkedServices: mappedLinks,
                      input: {
                        name: carSnapshot.name,
                        netValue: carSnapshot.netValue,
                        runtime: carSnapshot.runtime,
                        yearlyInsurance: carSnapshot.yearlyInsurance,
                        yearlyTax: carSnapshot.yearlyTax,
                        yearlyKilometers: carSnapshot.yearlyKilometers,
                        consumption: carSnapshot.consumption,
                        fuelPrice: carSnapshot.fuelPrice,
                        repairPerKilometer: carSnapshot.repairPerKilometer,
                        quantity: carSnapshot.quantity,
                        margin: carSnapshot.margin,
                      },
                    },
                    onCompleted: () => resolve(),
                    onError: (error) => reject(error),
                  });
                })
              );
            } catch (error) {
              console.error(`Failed to recreate car ${carSnapshot.id} after 3 attempts:`, error);
              newFailedAssets.push({ id: carSnapshot.id, name: carSnapshot.name, type: "car" });
            }
          }

          // Recreate purchases
          for (const purchaseSnapshot of purchasesSnapshotRef.current) {
            // Skip purchases that failed to remove
            if (failedToRemoveCarsPurchases.some(f => f.id === purchaseSnapshot.id && f.type === "purchase")) {
              continue;
            }

            const mappedLinks: ServiceLink[] = [];
            let hasMissingMapping = false;

            // Map old service IDs to new service IDs
            purchaseSnapshot.links.forEach((link) => {
              const newServiceId = serviceIdMapRef.current.get(link.serviceId);
              if (!newServiceId) {
                hasMissingMapping = true;
                return;
              }
              mappedLinks.push({ serviceId: newServiceId, ratio: link.ratio });
            });

            if (hasMissingMapping && purchaseSnapshot.links.length > 0) {
              // Only fail if there were links but we couldn't map them
              newFailedAssets.push({ id: purchaseSnapshot.id, name: purchaseSnapshot.name, type: "purchase" });
              continue;
            }

            try {
              await retryMutation(() =>
                new Promise<void>((resolve, reject) => {
                  addPurchaseMutation({
                    variables: {
                      tenderId: Number(tenderId),
                      linkedServices: mappedLinks,
                      input: {
                        name: purchaseSnapshot.name,
                        purchaseCost: purchaseSnapshot.purchaseCost,
                        monthsDepreciation: purchaseSnapshot.monthsDepreciation,
                        yearlyMaintenance: purchaseSnapshot.yearlyMaintenance,
                        yearlyRepair: purchaseSnapshot.yearlyRepair,
                        yearlyAdditionals: purchaseSnapshot.yearlyAdditionals,
                        quantity: purchaseSnapshot.quantity,
                        margin: purchaseSnapshot.margin,
                      },
                    },
                    onCompleted: () => resolve(),
                    onError: (error) => reject(error),
                  });
                })
              );
            } catch (error) {
              console.error(`Failed to recreate purchase ${purchaseSnapshot.id} after 3 attempts:`, error);
              newFailedAssets.push({ id: purchaseSnapshot.id, name: purchaseSnapshot.name, type: "purchase" });
            }
          }

          if (newFailedAssets.length > 0) {
            setFailedToRelinkAssets(newFailedAssets);
          }

          // After all services are processed, move to done state
          if (completedRecalculationsRef.current === services.length) {
            setProgressState(ProgressState.Done);
            tenderDataRefetch({
              id: Number(tenderId)
            });
          }
        } finally {
          isProcessingRef.current = false;
        }
      };

      // Start processing services
      processServices();
    }
  }, [
    progressState,
    services,
    isOpen,
    removeServiceMutation,
    removeCarMutation,
    removePurchaseMutation,
    updateTenderContractDurationMutation,
    addPermanentServiceMutation,
    addMobileServiceMutation,
    addCarMutation,
    addPurchaseMutation,
    editPermanentServiceMutation,
    editMobileServiceMutation,
    tenderId,
    servicesLoading,
    carsLoading,
    purchasesLoading,
    contractDurationValues.startDate,
    contractDurationValues.endDate,
    failedToRecalculate.length,
    failedToRemoveCarsPurchases,
    tenderDataRefetch,
  ]);

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setIsEditing(false);
        }
        if (progressState === ProgressState.Done || progressState === ProgressState.Failed) {
          setIsOpen(open);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("modals:servicerecalculation.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("modals:servicerecalculation.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2">
          {
            progressState !== ProgressState.Failed ? (
              <>
                {progressState === ProgressState.LoadingServices && <p>{t("modals:servicerecalculation.progressstate.loading")}</p>}
                {progressState === ProgressState.RemovingCarsAndPurchases && <p>{t("modals:servicerecalculation.progressstate.removingcarsandpurchases")}</p>}
                {progressState === ProgressState.RemovingServices && <p>{t("modals:servicerecalculation.progressstate.removing")}</p>}
                {progressState === ProgressState.UpdatingContractDuration && <p>{t("modals:servicerecalculation.progressstate.updating")}</p>}
                {progressState === ProgressState.RecalculatingServices && <p>{t("modals:servicerecalculation.progressstate.recalculating")}</p>}
                {progressState === ProgressState.Done && (
                  <p className={cn((failedToRecalculate.length === 0 && failedToRelinkAssets.length === 0 && failedToRemoveCarsPurchases.length === 0) ? "text-green-500" : "text-red-500")}>
                    {t("modals:servicerecalculation.progressstate.done")}
                  </p>
                )}

                <Progress
                  value={
                    progressState === ProgressState.LoadingServices ? 10 :
                      progressState === ProgressState.RemovingCarsAndPurchases ?
                        (carsSnapshotRef.current.length + purchasesSnapshotRef.current.length > 0
                          ? 10 + (20 * (completedCarPurchaseRemovalsRef.current / (carsSnapshotRef.current.length + purchasesSnapshotRef.current.length)))
                          : 30) :
                        progressState === ProgressState.RemovingServices ? 30 :
                          progressState === ProgressState.UpdatingContractDuration ? 50 :
                            progressState === ProgressState.RecalculatingServices
                              ? 50 + (40 * (completedRecalculationsRef.current / services.length)) :
                              progressState === ProgressState.Done ? 100 : 0
                  }
                  indicatorClassName={cn(progressIndicatorClassName)}
                />

                {progressState === ProgressState.RecalculatingServices && currentServiceName && (
                  <p className="text-sm text-muted-foreground">
                    {t("modals:servicerecalculation.serviceprogress", {
                      current: completedRecalculationsRef.current + 1,
                      total: services.length
                    })}
                    <span className="font-semibold text-slate-600 ml-1">
                      {currentServiceName}
                    </span>
                  </p>
                )}

                {failedToRecalculate.length > 0 && (
                  <Card className="shadow-none border-red-500 px-4 py-2 text-sm text-red-500 bg-red-50">
                    <p className="font-semibold mb-1">{t("modals:servicerecalculation.failed.services")}</p>
                    <ul className="list-disc list-inside">
                      {failedToRecalculate.map(service => (
                        <li key={service.id}>{service.name}</li>
                      ))}
                    </ul>
                  </Card>
                )}

                {failedToRemoveCarsPurchases.length > 0 && (
                  <Card className="shadow-none border-red-500 px-4 py-2 text-sm text-red-500 bg-red-50">
                    <p className="font-semibold mb-1">{t("modals:servicerecalculation.failed.removeassets")}</p>
                    <ul className="list-disc list-inside">
                      {failedToRemoveCarsPurchases.map((asset) => (
                        <li key={`remove-${asset.type}-${asset.id}`}>
                          {asset.name}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}

                {failedToRelinkAssets.length > 0 && (
                  <Card className="shadow-none border-red-500 px-4 py-2 text-sm text-red-500 bg-red-50">
                    <p className="font-semibold mb-1">{t("modals:servicerecalculation.failed.assets")}</p>
                    <ul className="list-disc list-inside">
                      {failedToRelinkAssets.map((asset) => (
                        <li key={`${asset.type}-${asset.id}`}>
                          {asset.name}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </>
            ) : (
              <Card className="flex flex-col gap-1 text-sm shadow-none border-destructive px-4 py-2 text-destructive bg-red-50">
                <h4 className="font-semibold">{t("modals:servicerecalculation.failed.title")}</h4>
              </Card>
            )
          }
        </div>
        <AlertDialogFooter>
          {(progressState === ProgressState.Done || progressState === ProgressState.Failed) && (
            <AlertDialogCancel asChild>
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                {t("common:buttons.close")}
              </Button>
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ServiceRecalculationDialog;
